import React from 'react';

import { EventList } from '@/components/domain-events-v2/EventList';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetAllEventsQuery } from '@/services/api/eventsService';

interface UserEventsTabProps {
  userId: string;
}

export const UserEventsTab: React.FC<UserEventsTabProps> = ({ userId }) => {
  const { data, isLoading, error } = useGetAllEventsQuery({
    user: userId,
    limit: 50,
    ordering: ['-occurred_at'],
  });

  if (isLoading) {
    return (
      <div className="space-y-8 py-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4 opacity-50">
            <Skeleton className="h-4 w-24 bg-zinc-100" />
            <div className="space-y-3 pl-4 border-l-2 border-zinc-100">
              <Skeleton className="h-20 w-full rounded-2xl bg-zinc-50" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center rounded-2xl bg-red-50/50 border border-red-100">
        <p className="text-red-600 font-medium">Failed to load event history</p>
        <p className="text-red-400 text-sm mt-1">Please try again later</p>
      </div>
    );
  }

  const events = data?.results || [];

  if (events.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-50 mb-4">
          <span className="text-xl grayscale opacity-30">📅</span>
        </div>
        <p className="text-zinc-900 font-medium">No activity recorded</p>
        <p className="text-zinc-500 text-sm mt-1">
          This user hasn&apos;t performed any actions yet.
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[600px] overflow-y-auto py-4 pr-2 scrollbar-thin scrollbar-thumb-zinc-200 scrollbar-track-transparent hover:scrollbar-thumb-zinc-300">
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <EventList events={events as any} />
    </div>
  );
};
